import type { ComponentConfig } from "@puckeditor/core"
import { SectionBase, sectionStyle } from "../lib/section"

export type PricingPlan = {
  name: string
  price: string
  period: string
  description: string
  features: { text: string }[]
  ctaLabel: string
  highlighted: "yes" | "no"
}

export type PricingProps = SectionBase & {
  eyebrow: string
  title: string
  subtitle: string
  plans: PricingPlan[]
}

const bgOptions = [
  { value: "none", label: "Transparent" },
  { value: "muted", label: "Muted" },
  { value: "accent", label: "Dark" },
] as const

const padOptions = [
  { value: "none", label: "None" },
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" },
] as const

export const Pricing: ComponentConfig<PricingProps> = {
  fields: {
    eyebrow: { type: "text", label: "Eyebrow", contentEditable: true },
    title: { type: "textarea", contentEditable: true },
    subtitle: { type: "textarea", contentEditable: true },
    plans: {
      type: "array",
      arrayFields: {
        name: { type: "text", label: "Plan name", contentEditable: true },
        price: { type: "text", label: "Price", contentEditable: true },
        period: { type: "text", label: "Period", contentEditable: true },
        description: { type: "text", label: "Description", contentEditable: true },
        ctaLabel: { type: "text", label: "CTA label", contentEditable: true },
        highlighted: {
          type: "select",
          options: [
            { label: "Normal", value: "no" },
            { label: "Highlighted", value: "yes" },
          ],
        },
        features: {
          type: "array",
          arrayFields: { text: { type: "text", contentEditable: true } },
          defaultItemProps: { text: "Feature" },
          getItemSummary: (f) => f?.text || "Item",
        },
      },
      defaultItemProps: {
        name: "Starter",
        price: "$0",
        period: "/month",
        description: "For small teams",
        ctaLabel: "Get started",
        highlighted: "no",
        features: [],
      },
      getItemSummary: (item) => item?.name || "Plan",
    },
    background: { type: "radio", options: bgOptions },
    paddingY: { type: "radio", options: padOptions },
  },

  defaultProps: {
    eyebrow: "",
    title: "Simple pricing",
    subtitle: "Choose the plan that fits your needs.",
    plans: [],
    background: "muted",
    paddingY: "lg",
  },

  render: ({ eyebrow, title, subtitle, plans, background, paddingY }) => {
    const s = sectionStyle({ background, paddingY })

    return (
      <section style={s}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            {eyebrow && (
              <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--site-accent, #7c3aed)", marginBottom: 12 }}>
                {eyebrow}
              </p>
            )}
            <h2 style={{ fontSize: "clamp(1.75rem,4vw,2.5rem)", fontWeight: 800, marginBottom: 16 }}>{title}</h2>
            {subtitle && <p style={{ fontSize: "1.125rem", color: "#6b7280" }}>{subtitle}</p>}
          </div>
          {plans.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(plans.length, 3)}, 1fr)`,
                gap: 24,
                alignItems: "start",
              }}
            >
              {plans.map((plan, i) => {
                const hl = plan.highlighted === "yes"
                return (
                  <div
                    key={i}
                    style={{
                      padding: 32,
                      background: hl ? "var(--site-accent, #7c3aed)" : "#fff",
                      color: hl ? "#fff" : "inherit",
                      borderRadius: 16,
                      border: hl ? "none" : "1px solid #e5e7eb",
                      boxShadow: hl ? "0 8px 32px rgba(124,58,237,0.25)" : "none",
                    }}
                  >
                    <h3 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: 8 }}>{plan.name}</h3>
                    <p style={{ color: hl ? "rgba(255,255,255,0.75)" : "#6b7280", marginBottom: 16, fontSize: 14 }}>{plan.description}</p>
                    <div style={{ marginBottom: 24 }}>
                      <span style={{ fontSize: "2.5rem", fontWeight: 800 }}>{plan.price}</span>
                      <span style={{ fontSize: 14, opacity: 0.75 }}>{plan.period}</span>
                    </div>
                    <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px 0" }}>
                      {(plan.features || []).map((f, j) => (
                        <li key={j} style={{ padding: "6px 0", fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ color: hl ? "#c4b5fd" : "#7c3aed" }}>✓</span>
                          {f.text}
                        </li>
                      ))}
                    </ul>
                    <button
                      style={{
                        width: "100%",
                        padding: 12,
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 15,
                        background: hl ? "#fff" : "var(--site-accent, #7c3aed)",
                        color: hl ? "var(--site-accent, #7c3aed)" : "#fff",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      {plan.ctaLabel}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>
    )
  },
}
